/**
 * Created by jonas on 2021-02-09.
 *
 * Super basic Symbol implementation that extends PIXI.Sprite
 *
 */

import {Possessed} from "../Possessed";
import {PossessedGameAssets} from "../PossessedGameAssets";
import {gsap} from 'gsap';
import {SpineGSAPWrapper} from '../animation/SpineGSAPWrapper';
import {PossessedStatics} from "../config/PossessedStatics";
import {PossessedWinConfig} from "../config/PossessedWinConfig";

export enum PossessedSymbolStates {
    NORMAL,
    SPIN,
    STOP,  // When reel is stopping
    WIN,
    NO_WIN
}

export interface PossessedSymbolCreateArgs {
    name:string,
    initialState:PossessedSymbolStates,
    reelID:number,
    reelPosition:number
}

export class PossessedSymbol extends PIXI.Container {
    public spriteView:PIXI.Container;

    protected xWaysView:PIXI.Container;
    protected xWays:PIXI.Sprite | undefined;
    protected xWaysBg:PIXI.Sprite|undefined;
    public reelPosition:number;
    public reelID:number;

    protected commonRevealAnim:PIXI.spine.Spine;
    protected waysReveal:PIXI.spine.Spine;
   // protected winToggleAnim:PIXI.spine.Spine|undefined;

    protected   mainSprite:PIXI.Sprite;
    protected   spinSprite:PIXI.Sprite;
    //! Temporary overlay square for ugly placeholder animations
    public      overlay:PIXI.Sprite;

    public xOverlay:PIXI.Sprite;

    private fullCrossSymbol:boolean = false;
    get isFullCrossSymbol():boolean {
        return this.fullCrossSymbol;
    }

    private symbolState:PossessedSymbolStates;
    public getState():PossessedSymbolStates {
        return this.symbolState;
    }

    constructor(args:PossessedSymbolCreateArgs) {
        super();
        this.name = args.name;
        this.reelID = args.reelID;
        this.reelPosition = args.reelPosition;
        this.symbolState = args.initialState;

        this.spriteView = new PIXI.Container();
        this.spriteView.name = "MainView";
        this.addChild(this.spriteView);

        this.commonRevealAnim = new PIXI.spine.Spine(Possessed.assetLoader.getSpineAsset(PossessedGameAssets.COMMON_REVEAL_ANIMATION));
        this.commonRevealAnim.visible = false;
        this.addChild(this.commonRevealAnim);

        this.xWaysView = new PIXI.Container();
        this.xWaysView.name = "xWays View";
        this.addChild(this.xWaysView);

        this.waysReveal = new PIXI.spine.Spine(Possessed.assetLoader.getSpineAsset(PossessedGameAssets.MULTIPLIER_NUMBER_ANIMATION));
        this.waysReveal.visible = false;
        this.addChild(this.waysReveal);

        this.xOverlay = new PIXI.Sprite(Possessed.assetLoader.getTextures("TempXwayOverlay")[0]);
        this.xOverlay.anchor.set(0.5, 0.5);
        this.xOverlay.visible = false;
        this.addChild(this.xOverlay);

        this.overlay = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.overlay.anchor.set(0.5, 0.5);
        this.overlay.width = PossessedStatics.SYMBOL_WIDTH;
        this.overlay.height = PossessedStatics.SYMBOL_HEIGHT;
        this.overlay.alpha = 0;
        this.overlay.name = "Symbol Overlay";
        this.addChild(this.overlay);
    }

    public initDefaultSymbolSprites(initState:PossessedSymbolStates) {
        this.mainSprite = new PIXI.Sprite(Possessed.assetLoader.getTextures(PossessedGameAssets.getSymbolStateTexturePath(this.name))[0]);
        this.spinSprite = new PIXI.Sprite(Possessed.assetLoader.getTextures(PossessedGameAssets.getSymbolStateTexturePath(this.name, "_spin"))[0]);

        this.mainSprite.anchor.set(0.5, 0.5);
        this.spinSprite.anchor.set(0.5, 0.5);

        this.mainSprite.alpha = initState == PossessedSymbolStates.SPIN || initState == PossessedSymbolStates.STOP ? 0 : 1;
        this.spinSprite.alpha = initState == PossessedSymbolStates.SPIN || initState == PossessedSymbolStates.STOP ? 1 : 0;

        this.spriteView.addChild(this.mainSprite);
        this.spriteView.addChild(this.spinSprite);
    }

    public playWin(winRatio:number):gsap.core.Timeline {
        this.symbolState = PossessedSymbolStates.WIN;
        let anim = "smallW";
        const winConf = PossessedWinConfig.getWinConfig(winRatio);
        if(winConf.winLevel >= 3) {
            anim = "largeW";
        } else if(winConf.winLevel >= 2) {
            anim = "midW";
        }
        const tl = gsap.timeline();
        tl.add(Possessed.reelView.winFireView.playWinFireAt(winRatio, this.reelID, this.reelPosition));
        return tl;
    }

    public playWinToggle():gsap.core.Timeline {
        //if(!this.winToggleAnim) {
        //    const toggleSpine = this.name[0] == "M" ? PossessedGameAssets.SYMBOL_WIN_TOGGLE_MEDIUM : PossessedGameAssets.SYMBOL_WIN_TOGGLE_LOW;
        //    this.winToggleAnim = new PIXI.spine.Spine(Possessed.assetLoader.getSpineAsset(toggleSpine));
        //    this.winToggleAnim.visible = false;
        //    this.addChildAt(this.winToggleAnim, this.children.indexOf(this.winAnim));
        //}
        const tl = gsap.timeline();
        //tl.add(() => {
        //    if(this.winToggleAnim) {
        //        SpineGSAPWrapper.resetSpineState(this.winToggleAnim);
        //        this.winToggleAnim.skeleton.setSkinByName(this.name);
        //    }
        //})
        tl.add(Possessed.reelView.winFireView.playWinToggleAt(this.reelID, this.reelPosition));
        //tl.add(SpineGSAPWrapper.createTimelineForAnimation(<PIXI.spine.Spine>this.winToggleAnim, 0, "zoom"), "<")
        //tl.add(()=>{
        //    if(this.winToggleAnim) this.winToggleAnim.visible = false;
        //})
        return tl;
    }

    public playNoWin():gsap.core.Timeline {
        this.symbolState = PossessedSymbolStates.NO_WIN;
        return gsap.timeline()
            .add(() => {
                this.overlay.tint = 0x000000;
            })
            .add(gsap.to(this.overlay, {alpha: 0.6, duration: 0.1}))
    }

    public playCommonReveal():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                SpineGSAPWrapper.resetSpineState(this.commonRevealAnim);
            })
            .add(SpineGSAPWrapper.createTimeline(this.commonRevealAnim, 0, "common", 0))
            .add(() => {
                this.commonRevealAnim.visible = false;
            })
    }
    playXwaysReveal():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                SpineGSAPWrapper.resetSpineState(this.commonRevealAnim);
            })
            .add(SpineGSAPWrapper.createTimeline(this.commonRevealAnim, 0, "xWay", 0))
            .add(() => {
                this.commonRevealAnim.visible = false;
            })
    }

    playWinIndication():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                SpineGSAPWrapper.resetSpineState(this.commonRevealAnim);
            })
            .add(SpineGSAPWrapper.createTimelineForAnimation(this.commonRevealAnim, 0, "xIndication"))
            .add(() => {
                this.commonRevealAnim.visible = false;
            })
    }

    playRevealIndication():gsap.core.Timeline {
        return gsap.timeline()
            .add(() => {
                SpineGSAPWrapper.resetSpineState(this.commonRevealAnim);
            })
            .add(SpineGSAPWrapper.createTimeline(this.commonRevealAnim, 0, "xIndication", 0))
    }

    playWaysReveal(ways:number, playSound:boolean=false):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            SpineGSAPWrapper.resetSpineState(this.waysReveal);
        })
        tl.add(SpineGSAPWrapper.createTimeline(this.waysReveal, 0, `X${ways}s`));
        if(playSound) {
            tl.add(() => {Possessed.soundPlayer.playSound("xwaysInfInfect")}, "<");
        }
        tl.add(() => {
            this.setXWays(ways);
            this.waysReveal.visible = false;
        })
        return tl;
    }

    public setNormal():gsap.core.Timeline {
        this.symbolState = PossessedSymbolStates.NORMAL;
        Possessed.reelView.reelModels[this.reelID].addChild(this);
        const tl = gsap.timeline();
        tl.add(gsap.to(this.overlay, {alpha: 0, duration: 0.2}), "<")
        return tl;
    }

    public moveToWinLayer() {
        //Possessed.reelView.reelModels[this.reelID].removeChild(this);
        Possessed.reelView.winSymbolsView.addSymbol(this);
    }
    public moveToNormalLayer() {
        Possessed.reelView.reelModels[this.reelID].addChild(this);
    }

    public setXWays(ways:number) {
        // If we already have set ways on symbol we remove it as child
        if(this.xWays && this.xWaysView.children.find(c => c == this.xWays)) this.xWaysView.removeChildren(0);

        this.xWaysBg = new PIXI.Sprite(Possessed.assetLoader.getTextures("xPattern")[0]);
        this.xWaysBg.anchor.set(0.5, 0.5);
        //this.xWaysBg.scale.set(0.5, 0.5);
        this.xWaysBg.position.set(29, 14);

        this.xWays = new PIXI.Sprite(Possessed.assetLoader.getTextures(`X${ways}`)[0]);
        // Line up xway text with split animation
        this.xWays.scale.set(0.5, 0.5);
        this.xWays.position.set(13, 5);

        this.xWaysView.addChild(this.xWaysBg, this.xWays);
    }

    public crossFadeToSpin(fadeDur:number=0.5):gsap.core.Timeline {
        if(!this.fullCrossSymbol) {
            return gsap.timeline({
                onStart: () => {
                    this.spinSprite.alpha = 0;
                    this.mainSprite.alpha = 1;
                }
            }).add(gsap.to(this.spinSprite, {alpha: 1, duration: fadeDur}), "<")
                .add(gsap.to(this.mainSprite, {alpha: 0, duration: fadeDur}), "<")
        } else {
            return gsap.timeline();
        }
    }
    public crossFadeFromSpin(fadeDur:number=0.5):gsap.core.Timeline {
        return gsap.timeline({
            onStart: () => {
                this.spinSprite.alpha = 1;
                this.mainSprite.alpha = 0;
            }
        }).add(gsap.to(this.spinSprite, {alpha: 0, duration: fadeDur}), "<")
            .add(gsap.to(this.mainSprite, {alpha: 1, duration: fadeDur}), "<")
    }

    public getLandAnimation():gsap.core.Timeline {
        const tl =  gsap.timeline();
        if(this.name == "XW") {
            tl.add(() => {
                Possessed.soundPlayer.playSoundNoOverlapDavidPanning("xwaysLand", this.reelID);
                SpineGSAPWrapper.resetSpineState(this.commonRevealAnim);
            })
            tl.add(SpineGSAPWrapper.createTimeline(this.commonRevealAnim, 0, "xWayland", 0), "<")
            tl.add(() => {
                this.commonRevealAnim.state.setAnimation(0, "xwayActivate", true);
            })
        }
        return tl;
    }

    public turnIntoFullCross() {
        if(this.name[0] != "M") return;
        this.mainSprite.texture = Possessed.assetLoader.getTextures(`Cross syms/${this.name}_${this.reelID}_${this.reelPosition}`)[0];
        this.spinSprite.texture = Possessed.assetLoader.getTextures(`Cross syms/${this.name}_${this.reelID}_${this.reelPosition}`)[0];
        this.fullCrossSymbol = true;
    }

    public getAbsolutePosition():PIXI.Point {
        const parentTransform = this.parent?.transform;

        return new PIXI.Point(
            (parentTransform ? parentTransform.position.x : 0) + this.position.x,
            (parentTransform ? parentTransform.position.y : 0) + this.position.y
        );
    }

    public resetState() {
        this.moveToNormalLayer();
        this.setNormal();
    }
}